/**
 * What Probe's sidebar lists, as a reader sees it: the call that is up,
 * pinned, then the saved interviews newest first, each by its name
 * (`interviewName`: the review's title, else the debrief's first area, else
 * the opening question), with its outcome and its day.
 *
 * @module components/apps/probe/sidebar/ProbeSidebar.logic
 */
import type { ProbeOutcome } from "@amoreg/contracts";
import { interviewName } from "@amoreg/shared/probePractice";

import type { ProbeInterviewSummary } from "../../../../state/probeHistory";
import { formatProbeDuration, OUTCOME_LABELS } from "../probeArea";
import type { ProbeSection } from "../probePaths";

export interface ProbeSidebarLive {
  /** When the call came up, for its running time; null when the page has not said. */
  readonly startedAt: number | null;
  /** The stage, where the call is, is open. */
  readonly active: boolean;
}

export interface ProbeSidebarInterview {
  readonly id: string;
  readonly title: string;
  /** Null for an interview saved before debriefs, or whose debrief failed. */
  readonly outcome: ProbeOutcome | null;
  /** The day: `Today`, `Yesterday`, a weekday this past week, then the date. */
  readonly day: string;
  /** Length and outcome in words, for the row's tooltip and its accessible name. */
  readonly detail: string;
  readonly active: boolean;
}

export interface ProbeSidebarView {
  readonly live: ProbeSidebarLive | null;
  /** The listed interviews, newest first. */
  readonly interviews: ReadonlyArray<ProbeSidebarInterview>;
  /** Every saved interview, listed or not, for All interviews. */
  readonly total: number;
}

export interface ProbeSidebarInput {
  readonly interviews: ReadonlyArray<ProbeInterviewSummary>;
  readonly live: { readonly startedAt: number | null } | null;
  readonly section: ProbeSection | null;
  readonly nowMs: number;
  /** How many to list; the rest are on the Interviews page. */
  readonly limit: number;
}

const startOfDay = (ms: number): number => {
  const date = new Date(ms);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

/** Whole calendar days from `ms` to `nowMs`; a DST day still counts as one. */
const daysAgo = (ms: number, nowMs: number): number =>
  Math.round((startOfDay(nowMs) - startOfDay(ms)) / 86_400_000);

const shortDate = (ms: number, nowMs: number): string => {
  const date = new Date(ms);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date(nowMs).getFullYear() ? {} : { year: "numeric" }),
  });
};

/** `Today`, `Yesterday`, `Mon` within the week, then `Sep 21`. */
export const sidebarDay = (ms: number, nowMs: number): string => {
  const days = daysAgo(ms, nowMs);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return new Date(ms).toLocaleDateString(undefined, { weekday: "short" });
  return shortDate(ms, nowMs);
};

/** `0:42`, `4:05`, `1:02:10`: how long the call has been up. */
export const liveElapsed = (startedAt: number, nowMs: number): string => {
  const seconds = Math.max(0, Math.floor((nowMs - startedAt) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
};

export function probeSidebarView(input: ProbeSidebarInput): ProbeSidebarView {
  const { section, nowMs } = input;
  const openId = section?.kind === "interview" ? section.interviewId : null;
  const interviews = input.interviews
    .slice(0, input.limit)
    .map((interview): ProbeSidebarInterview => {
      const created = Date.parse(interview.createdAt);
      const outcome = interview.debrief?.outcome ?? null;
      return {
        id: interview.id,
        title: interviewName(interview),
        outcome,
        day: Number.isFinite(created) ? sidebarDay(created, nowMs) : "",
        detail: [
          interview.durationMs === null ? null : formatProbeDuration(interview.durationMs),
          outcome === null ? null : OUTCOME_LABELS[outcome],
        ]
          .filter((part) => part !== null)
          .join(" · "),
        active: openId === interview.id,
      };
    });

  return {
    live:
      input.live === null
        ? null
        : { startedAt: input.live.startedAt, active: section?.kind === "stage" },
    interviews,
    total: input.interviews.length,
  };
}
